import type { Node, TextureNode } from 'three/webgpu'
import {
  CLIP_STATE_HEADER_TEXELS,
  CLIP_STATE_TEXTURE_WIDTH,
  type ClipStatePacket,
} from '@/shared/clipping/ClipStatePacket'
import {
  type CompiledClipPolygon,
  type CompiledClipState,
  type CompiledClipVolume,
  CompiledClipVolumeKind,
  MAX_COMPILED_CLIP_VOLUMES,
} from '@/shared/clipping/CompiledClipState'
import { ClipPolygonMaxVerticeCount, ClipTask } from '@/shared/constants'

export type TSLModule = typeof import('three/tsl')

/** 动态裁切求值实际消费的最小 TSL API，供 WebGPU compute kernel 复用。 */
export type TSLClipModule = Pick<
  TSLModule,
  | 'If'
  | 'bitOr'
  | 'bool'
  | 'float'
  | 'int'
  | 'ivec2'
  | 'mat4'
  | 'shiftLeft'
  | 'texture'
  | 'vec2'
  | 'vec4'
>

/** 单个世界点经过 TSL 裁切规则计算后的节点。 */
export interface TSLClipEvaluationNodes {
  readonly hidden: Node<'bool'>
  readonly highlighted: Node<'bool'>
}

export interface TSLClipIntUniformNode extends Node<'int'> {
  value: number
}

/** 逐 View 可切换的 TSL 裁切数据绑定。 */
export interface TSLClipStateBindingNodes {
  readonly texture: TextureNode
  readonly volumeCount: TSLClipIntUniformNode
}

function readTexel(tsl: TSLClipModule, packet: ClipStatePacket, column: number, row: number) {
  const uv = tsl.vec2(
    (column + 0.5) / CLIP_STATE_TEXTURE_WIDTH,
    (row + 0.5) / Math.max(1, packet.texture.image.height)
  )
  return tsl.texture(packet.texture, uv)
}

function readMatrix(tsl: TSLClipModule, packet: ClipStatePacket, row: number) {
  return tsl.mat4(
    readTexel(tsl, packet, 0, row),
    readTexel(tsl, packet, 1, row),
    readTexel(tsl, packet, 2, row),
    readTexel(tsl, packet, 3, row)
  )
}

function evaluateBoxVolume(
  tsl: TSLClipModule,
  packet: ClipStatePacket,
  volumeIndex: number,
  worldPoint: Node<'vec3'>
) {
  const boxPoint = readMatrix(tsl, packet, volumeIndex).mul(tsl.vec4(worldPoint, 1)).xyz
  const min = -0.5 - 1e-5
  const max = 0.5 + 1e-5
  return boxPoint.x
    .greaterThanEqual(min)
    .and(boxPoint.x.lessThanEqual(max))
    .and(boxPoint.y.greaterThanEqual(min))
    .and(boxPoint.y.lessThanEqual(max))
    .and(boxPoint.z.greaterThanEqual(min))
    .and(boxPoint.z.lessThanEqual(max))
}

function evaluatePolygonVolume(
  tsl: TSLClipModule,
  packet: ClipStatePacket,
  volumeIndex: number,
  volume: CompiledClipPolygon,
  worldPoint: Node<'vec3'>
) {
  const epsilon = tsl.float(1e-5)
  const projected = readMatrix(tsl, packet, volumeIndex).mul(tsl.vec4(worldPoint, 1))
  const point = projected.xy.div(projected.w)
  const inside = tsl.bool(false).toVar()

  for (let vertexIndex = 0; vertexIndex < volume.vertices.length; vertexIndex += 1) {
    const previous =
      vertexIndex === 0
        ? volume.vertices[volume.vertices.length - 1]!
        : volume.vertices[vertexIndex - 1]!
    const current = volume.vertices[vertexIndex]!
    const start = tsl.vec2(previous.x, previous.y)
    const end = tsl.vec2(current.x, current.y)
    const edge = end.sub(start)
    const offset = point.sub(start)
    const edgeLengthSq = edge.dot(edge)
    const crossValue = edge.x.mul(offset.y).sub(edge.y.mul(offset.x))
    const projection = offset.dot(edge)
    const onSegment = edgeLengthSq
      .lessThanEqual(epsilon.mul(epsilon))
      .and(offset.dot(offset).lessThanEqual(epsilon.mul(epsilon)))
      .or(
        crossValue
          .abs()
          .lessThanEqual(epsilon.mul(edgeLengthSq.sqrt().max(1)))
          .and(projection.greaterThanEqual(epsilon.negate()))
          .and(projection.lessThanEqual(edgeLengthSq.add(epsilon)))
      )
    tsl.If(onSegment, () => inside.assign(true))

    const currentAbove = tsl.float(current.y).greaterThan(point.y)
    const previousAbove = tsl.float(previous.y).greaterThan(point.y)
    const crossesY = currentAbove.and(previousAbove.not()).or(currentAbove.not().and(previousAbove))
    const intersectionX = tsl
      .float(previous.x)
      .sub(current.x)
      .mul(point.y.sub(current.y))
      .div(previous.y - current.y)
      .add(current.x)
    tsl.If(crossesY.and(point.x.lessThan(intersectionX)), () => inside.assign(inside.not()))
  }

  return projected.w.greaterThan(epsilon).and(inside)
}

function evaluateVolume(
  tsl: TSLClipModule,
  packet: ClipStatePacket,
  volumeIndex: number,
  volume: CompiledClipVolume,
  worldPoint: Node<'vec3'>
) {
  return volume.kind === CompiledClipVolumeKind.BOX
    ? evaluateBoxVolume(tsl, packet, volumeIndex, worldPoint)
    : evaluatePolygonVolume(tsl, packet, volumeIndex, volume, worldPoint)
}

/** 按共享 CPU 语义创建裁切判定节点。 */
export function createTslClipEvaluation(
  tsl: TSLClipModule,
  packet: ClipStatePacket,
  state: CompiledClipState,
  worldPoint: Node<'vec3'>
): TSLClipEvaluationNodes {
  const hidden = tsl.bool(false).toVar()
  const highlighted = tsl.bool(false).toVar()
  const insideMask = tsl.int(0).toVar()
  const matchedMask = tsl.int(0).toVar()

  for (let volumeIndex = 0; volumeIndex < state.volumes.length; volumeIndex += 1) {
    const volume = state.volumes[volumeIndex]!
    const inside = evaluateVolume(tsl, packet, volumeIndex, volume, worldPoint)
    const groupBit = tsl.int(1 << volume.groupIndex)

    if (volume.clipTask === ClipTask.SHOW_OUTSIDE) {
      tsl.If(inside, () => {
        hidden.assign(true)
        highlighted.assign(false)
      })
    } else if (volume.clipTask === ClipTask.SHOW_INSIDE) {
      insideMask.assign(tsl.bitOr(insideMask, groupBit))
      tsl.If(inside, () => matchedMask.assign(tsl.bitOr(matchedMask, groupBit)))
    } else if (volume.clipTask === ClipTask.HIGHLIGHT) {
      tsl.If(inside, () => highlighted.assign(true))
    }
  }

  tsl.If(insideMask.notEqual(matchedMask), () => hidden.assign(true))
  return { hidden, highlighted }
}

function readDynamicTexel(
  tsl: TSLClipModule,
  texture: TextureNode,
  column: Node<'int'> | number,
  row: Node<'int'> | number
) {
  return tsl.texture(texture).load(tsl.ivec2(column, row))
}

function readDynamicMatrix(tsl: TSLClipModule, texture: TextureNode, row: Node<'int'> | number) {
  return tsl.mat4(
    readDynamicTexel(tsl, texture, 0, row),
    readDynamicTexel(tsl, texture, 1, row),
    readDynamicTexel(tsl, texture, 2, row),
    readDynamicTexel(tsl, texture, 3, row)
  )
}

function evaluateDynamicPolygonVolume(
  tsl: TSLClipModule,
  texture: TextureNode,
  volumeIndex: Node<'int'>,
  vertexCount: Node<'int'>,
  worldPoint: Node<'vec3'>,
  maxVertexCount: number
) {
  const epsilon = tsl.float(1e-5)
  const projected = readDynamicMatrix(tsl, texture, volumeIndex).mul(tsl.vec4(worldPoint, 1))
  const point = projected.xy.div(projected.w)
  const inside = tsl.bool(false).toVar()
  // TSL may reuse the same temporary across unrolled volume blocks; reset per volume explicitly.
  inside.assign(false)
  const projectionValid = projected.w.greaterThan(epsilon)

  for (let vertexIndex = 0; vertexIndex < maxVertexCount; vertexIndex += 1) {
    const currentIndex = tsl.int(vertexIndex)
    const active = projectionValid.and(currentIndex.lessThan(vertexCount))
    const previousIndex = currentIndex.equal(0).select(vertexCount.sub(1), currentIndex.sub(1))
    const previous = readDynamicTexel(
      tsl,
      texture,
      tsl.int(CLIP_STATE_HEADER_TEXELS).add(previousIndex),
      volumeIndex
    ).xy
    const current = readDynamicTexel(
      tsl,
      texture,
      tsl.int(CLIP_STATE_HEADER_TEXELS).add(currentIndex),
      volumeIndex
    ).xy
    const edge = current.sub(previous)
    const offset = point.sub(previous)
    const edgeLengthSq = edge.dot(edge)
    const crossValue = edge.x.mul(offset.y).sub(edge.y.mul(offset.x))
    const projection = offset.dot(edge)
    const onSegment = edgeLengthSq
      .lessThanEqual(epsilon.mul(epsilon))
      .and(offset.dot(offset).lessThanEqual(epsilon.mul(epsilon)))
      .or(
        crossValue
          .abs()
          .lessThanEqual(epsilon.mul(edgeLengthSq.sqrt().max(1)))
          .and(projection.greaterThanEqual(epsilon.negate()))
          .and(projection.lessThanEqual(edgeLengthSq.add(epsilon)))
      )
    const currentAbove = current.y.greaterThan(point.y)
    const previousAbove = previous.y.greaterThan(point.y)
    const crossesY = currentAbove.and(previousAbove.not()).or(currentAbove.not().and(previousAbove))
    const intersectionX = previous.x
      .sub(current.x)
      .mul(point.y.sub(current.y))
      .div(previous.y.sub(current.y))
      .add(current.x)

    inside.assign(active.and(onSegment).select(tsl.bool(true), inside))
    inside.assign(
      active.and(crossesY).and(point.x.lessThan(intersectionX)).select(inside.not(), inside)
    )
  }

  return projectionValid.and(inside)
}

function evaluateDynamicVolume(
  tsl: TSLClipModule,
  binding: TSLClipStateBindingNodes,
  volumeIndex: Node<'int'>,
  worldPoint: Node<'vec3'>,
  maxPolygonVertices: number
) {
  const metadata = readDynamicTexel(tsl, binding.texture, 5, volumeIndex)
  const kind = tsl.int(metadata.w.add(0.5))
  const vertexCount = tsl.int(metadata.x.add(0.5))
  const boxPoint = readDynamicMatrix(tsl, binding.texture, volumeIndex).mul(
    tsl.vec4(worldPoint, 1)
  ).xyz
  const min = -0.5 - 1e-5
  const max = 0.5 + 1e-5
  const boxInside = boxPoint.x
    .greaterThanEqual(min)
    .and(boxPoint.x.lessThanEqual(max))
    .and(boxPoint.y.greaterThanEqual(min))
    .and(boxPoint.y.lessThanEqual(max))
    .and(boxPoint.z.greaterThanEqual(min))
    .and(boxPoint.z.lessThanEqual(max))
  const polygonInside = evaluateDynamicPolygonVolume(
    tsl,
    binding.texture,
    volumeIndex,
    vertexCount,
    worldPoint,
    maxPolygonVertices
  )

  return kind.equal(CompiledClipVolumeKind.BOX).select(boxInside, polygonInside)
}

/**
 * 按共享 packet 语义创建可逐 View 切换 texture/count 的裁切判定节点。
 *
 * polygon 顶点在 WebGPU compute 中按当前 packet 的最大顶点数做编译期展开，避开
 * TSL LoopNode 在 compute Fn 内吞掉后续代码的问题；box-only packet 的展开数为 0。
 */
export function createDynamicTslClipEvaluation(
  tsl: TSLClipModule,
  binding: TSLClipStateBindingNodes,
  worldPoint: Node<'vec3'>,
  maxPolygonVertices: number,
  maxVolumes: number
): TSLClipEvaluationNodes {
  const hidden = tsl.bool(false).toVar()
  const highlighted = tsl.bool(false).toVar()
  const insideMask = tsl.int(0).toVar()
  const matchedMask = tsl.int(0).toVar()
  const vertexLimit = Math.max(
    0,
    Math.min(ClipPolygonMaxVerticeCount, Math.trunc(maxPolygonVertices))
  )
  const volumeLimit = Math.max(0, Math.min(MAX_COMPILED_CLIP_VOLUMES, Math.trunc(maxVolumes)))

  for (let volumeIndex = 0; volumeIndex < volumeLimit; volumeIndex += 1) {
    const active = tsl.int(volumeIndex).lessThan(binding.volumeCount)
    const metadata = readDynamicTexel(tsl, binding.texture, 5, volumeIndex)
    const clipTask = tsl.int(metadata.y.add(0.5))
    const groupIndex = tsl.int(metadata.z.add(0.5))
    const inside = evaluateDynamicVolume(
      tsl,
      binding,
      tsl.int(volumeIndex),
      worldPoint,
      vertexLimit
    )
    const groupBit = tsl.int(1).shiftLeft(groupIndex)
    const showOutside = active.and(clipTask.equal(ClipTask.SHOW_OUTSIDE)).and(inside)
    const showInside = active.and(clipTask.equal(ClipTask.SHOW_INSIDE))
    const highlight = active.and(clipTask.equal(ClipTask.HIGHLIGHT)).and(inside)

    hidden.assign(hidden.or(showOutside))
    highlighted.assign(showOutside.select(tsl.bool(false), highlighted))
    insideMask.assign(tsl.bitOr(insideMask, showInside.select(groupBit, tsl.int(0))))
    matchedMask.assign(tsl.bitOr(matchedMask, showInside.and(inside).select(groupBit, tsl.int(0))))
    highlighted.assign(highlighted.or(highlight))
  }

  hidden.assign(hidden.or(insideMask.notEqual(matchedMask)))
  highlighted.assign(hidden.select(tsl.bool(false), highlighted))
  return { hidden, highlighted }
}
